import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LlaveService } from 'src/app/shared/services/llave.service';
import { MarketingOrigenService } from 'src/app/shared/services/marketing-origen.service';
import { SignupStateService } from '../../services/signup-state.service';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { PASOS_COMUNES, toE164 } from '../../constants/signup.constants';
import { KycApiService } from 'src/app/auth/services/kyc-api.service';
import { PersonValidationService } from 'src/app/auth/services/person-validation.service';
import { OtpInputComponent } from '../shared/otp-input/otp-input.component';

/**
 * Cuánto hay que esperar antes de poder pedir otro código.
 *
 * NO es la vida del código: el de Cognito dura mucho más (horas), y quién lo da
 * por vencido es Cognito, que responde `ExpiredCodeException`. Este contador
 * existe solo para no dejar que alguien pida códigos en ráfaga.
 */
const RESEND_COOLDOWN_SECONDS = 300;

@Component({
  selector: 'app-email-otp-step',
  templateUrl: './email-otp-step.component.html',
  styleUrls: ['./email-otp-step.component.sass'],
})
export class EmailOtpStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  @ViewChild('otpInput') otpInput!: OtpInputComponent;

  // Estado
  email: string = '';
  loading: boolean = false;
  /** Cognito rechazó el código por vencido. Solo lo marca el backend. */
  isExpired: boolean = false;
  /** Se cumplió el cooldown y ya se puede pedir otro código. */
  canResend: boolean = false;
  showEditEmail: boolean = false;

  /** Segundos hasta poder reenviar. */
  countdown: number = RESEND_COOLDOWN_SECONDS;
  private countdownInterval: any;

  // Formulario para editar email
  editEmailForm!: FormGroup;
  editEmailLoading: boolean = false;

  constructor(
private fb: FormBuilder,
    public signupState: SignupStateService,
    private cognitoService: CognitoService,
    private kycService: KycApiService,
    private personValidationService: PersonValidationService,
    private llaveService: LlaveService,
    private marketingOrigen: MarketingOrigenService
  ) {}

  ngOnInit(): void {
    // Verificar que hay datos de formulario válidos
    const formData = this.signupState.currentState.formData;
    if (!formData?.email) {
      this.signupState.goToStep(PASOS_COMUNES.FORMULARIO);
      return;
    }

    // Si el email ya está verificado, avanzar automáticamente
    if (this.signupState.currentState.emailVerified) {
      this.signupState.nextStep();
      return;
    }

    this.email = formData.email;
    this.initEditEmailForm();

    // Restaurar el cooldown de reenvío desde el timestamp persistido
    const sentAt = this.signupState.currentState.emailOtpSentAt;
    if (sentAt) {
      const elapsed = Math.floor((Date.now() - sentAt) / 1000);
      const remaining = RESEND_COOLDOWN_SECONDS - elapsed;
      if (remaining <= 0) {
        this.canResend = true;
      } else {
        this.startCountdown(remaining);
      }
    } else {
      this.startCountdown();
    }
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  private initEditEmailForm(): void {
    this.editEmailForm = this.fb.group({
      newEmail: ['', [Validators.required, Validators.email]],
    });
  }

  // ==================== COUNTDOWN ====================

  private startCountdown(initialSeconds: number = RESEND_COOLDOWN_SECONDS): void {
    this.countdown = initialSeconds;
    this.isExpired = false;
    this.canResend = false;

    this.countdownInterval = setInterval(() => {
      this.countdown--;

      if (this.countdown <= 0) {
        this.stopCountdown();
        // Habilita pedir otro código. NO marca el actual como vencido: eso solo
        // lo sabe Cognito.
        this.canResend = true;
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  get countdownFormatted(): string {
    const minutes = Math.floor(this.countdown / 60);
    const seconds = this.countdown % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // ==================== CONFIRMAR OTP ====================

  async onCodeComplete(code: string): Promise<void> {
    if (this.loading) return;
    await this.confirmEmailOtp(code);
  }

  async confirmEmailOtp(code: string): Promise<void> {
    if (!code || code.length !== 6) return;

    // El código NO se bloquea desde acá.
    //
    // Antes el front lo daba por vencido a los 5 minutos, pero esa cuenta era
    // nuestra: el código de Cognito vive mucho más. Al usuario que tardaba en
    // abrir el correo le rebotábamos un código que el backend seguía aceptando y
    // lo obligábamos a pedir otro. Quien decide si venció es Cognito, que
    // responde `ExpiredCodeException` y lo maneja `handleConfirmError`.
    this.loading = true;

    try {
      // 1. Confirmar código en Cognito
      try {
        await this.cognitoService.confirmSignUp(this.email, code);
      } catch (confirmError: any) {
        // Si el usuario ya está CONFIRMED (registro previo parcial), no es error real
        const isAlreadyConfirmed =
          confirmError?.name === 'NotAuthorizedException' &&
          confirmError?.message?.includes('Current status is CONFIRMED');
        if (!isAlreadyConfirmed) {
          throw confirmError;
        }
      }

      // 2. Iniciar sesión en Cognito
      const password = this.signupState.currentState.formData?.password || '';
      await this.cognitoService.signIn(this.email, password);

      // 2.1. Marcar que el registro está en progreso (para evitar acceso al dashboard)
      sessionStorage.setItem('aguacate_registration_in_progress', 'true');

      // 3. Obtener cognitoId
      const user = await this.cognitoService.getUser();
      if (user?.username) {
        this.signupState.setCognitoId(user.username);
      }

      // 4. Marcar email como verificado
      this.signupState.setEmailVerified(true);

      // 4.1. Avisar que este correo llego por el link de alguien.
      //
      // Va aca y no al final del wizard a proposito: capturar en el submit
      // obligaria a una llamada mas justo cuando el usuario ya termino y esta
      // cerrando la app, y si esa llamada falla el referido se pierde sin que
      // nadie se entere. El correo recien quedo verificado, que es lo que el
      // backend usa para atar el vinculo cuando aparezca el alta.
      //
      // No frena nada: el servicio atrapa cualquier error.
      //
      // El correo no se manda: lo toma el backend del token de Cognito, que
      // existe desde el paso 2 de aca arriba. Mandarlo desde el cliente era la
      // forma de robarse el invitado de otro.
      const llaveReferidor = this.signupState.currentState.llaveReferidor;
      if (llaveReferidor) {
        await this.llaveService.registrarIntencion(llaveReferidor);
      }

      // 4.2. Lo mismo para el link de campana de Marketing: el correo sale
      // del token. Tampoco lanza.
      await this.marketingOrigen.registrarSiCorresponde();

      // 5. Solicitar OTP WhatsApp automáticamente
      await this.requestWhatsappOtp();

      // 6. Navegar al paso 4
      this.onSuccess.emit('Correo verificado correctamente');
      await this.signupState.nextStep();

    } catch (error: any) {
      this.handleConfirmError(error);
      this.otpInput?.triggerError();
      this.otpInput?.reset();
    } finally {
      this.loading = false;
    }
  }

  private handleConfirmError(error: any): void {
    const errorName = error?.name || error?.code || '';
    const errorMessage = error?.message || '';

    // ExpiredCodeException PRIMERO: su mensaje incluye "Invalid code provided"
    // que matcheaba incorrectamente con CodeMismatchException
    if (errorName === 'ExpiredCodeException' || errorMessage.includes('expired') || errorMessage.includes('please request a code again')) {
      this.isExpired = true;
      this.canResend = true;
      this.onError.emit('El código ha expirado. Solicita uno nuevo.');
    } else if (errorName === 'CodeMismatchException' || errorMessage.includes('Invalid')) {
      this.onError.emit('El código ingresado no es válido.');
    } else if (errorName === 'LimitExceededException') {
      this.onError.emit('Demasiados intentos. Espera un momento.');
    } else {
      this.onError.emit('Error al verificar el código. Intenta nuevamente.');
    }
  }

  private async requestWhatsappOtp(): Promise<void> {
    const formData = this.signupState.currentState.formData;
    if (!formData?.phoneCode || !formData?.phone) return;

    const phoneNumber = toE164(formData.phoneCode, formData.phone);

    try {
      const response = await this.kycService.sendOtp(phoneNumber);
      if (!response.success) {
        // Guardar el error (con su `code`) para que el paso 4 decida qué
        // mostrar: solo NOT_ON_WHATSAPP pide cambiar el número, el resto deja
        // reintentar. Los deadlines salen de la respuesta: en cualquier
        // rechazo el código anterior SIGUE vigente y, si existe, el backend
        // manda su vencimiento; lo que no venga no se pisa.
        this.signupState.setWhatsappOtpError(response.message, response.code);
        this.signupState.applyWhatsappOtpRejection(response);
      } else {
        // Limpiar cualquier error previo si el envío fue exitoso
        this.signupState.clearWhatsappOtpError();
        // Guardamos los deadlines que decidió el backend (vida del código y
        // cooldown de reenvío); el paso de WhatsApp arma su countdown con eso
        // en lugar de asumir el TTL.
        this.signupState.applyWhatsappOtpResponse(response);
      }
    } catch (error) {
      // `sendOtp` no lanza; esto cubre algo inesperado. Se trata como ERROR
      // (reintentable) y no se borran los deadlines: no sabemos si el backend
      // alcanzó a mandar un código, y el paso 4 sin vencimiento vigente ya
      // queda con el reenvío habilitado.
      this.signupState.setWhatsappOtpError('Error al enviar el código. Intente nuevamente.', 'ERROR');
    }
  }

  // ==================== REENVIAR CÓDIGO ====================

  async resendCode(): Promise<void> {
    if (!this.canResend && !this.isExpired) return;

    this.loading = true;

    try {
      await this.cognitoService.resendConfirmationCode(this.email);
      this.signupState.setEmailOtpSentAt(Date.now());
      this.onSuccess.emit('Código reenviado correctamente');
      this.startCountdown();
      this.otpInput?.reset();
    } catch (error: any) {
      this.onError.emit('No se pudo reenviar el código. Intenta más tarde.');
    } finally {
      this.loading = false;
    }
  }

  // ==================== EDITAR EMAIL ====================

  toggleEditEmail(): void {
    this.showEditEmail = !this.showEditEmail;
    if (this.showEditEmail) {
      this.editEmailForm.reset();
    }
  }

  async saveNewEmail(): Promise<void> {
    if (!this.editEmailForm.valid) return;

    const newEmail = this.editEmailForm.get('newEmail')?.value?.toLowerCase();
    if (newEmail === this.email.toLowerCase()) {
      this.onError.emit('El nuevo correo es igual al actual.');
      return;
    }

    this.editEmailLoading = true;

    try {
      // 1. Validar disponibilidad del nuevo email (personas y empresas)
      // El backend limpia usuarios no verificados automáticamente
      const validation = await this.personValidationService.validatePersonEmail(newEmail);
      if (!validation.isAvailable) {
        this.onError.emit(validation.message || 'Este correo no está disponible.');
        this.editEmailLoading = false;
        return;
      }

      // 2. Registrar nuevo email en Cognito
      const formData = this.signupState.currentState.formData;
      const password = formData?.password || '';
      const isCompany = this.signupState.isEmpresa ? 1 : 0;

      await this.cognitoService.signUp(newEmail, password, isCompany, 'aguacate_client');

      // 3. Actualizar estado con nuevo email
      this.signupState.setFormData({
        ...formData!,
        email: newEmail,
      });

      this.email = newEmail;
      this.showEditEmail = false;
      this.signupState.setEmailOtpSentAt(Date.now());
      this.onSuccess.emit('Código enviado al nuevo correo');
      this.startCountdown();
      this.otpInput?.reset();

    } catch (error: any) {
      this.handleSaveEmailError(error, newEmail);
    } finally {
      this.editEmailLoading = false;
    }
  }

  /**
   * Maneja errores específicos de Cognito al cambiar email
   * Errores posibles:
   * - UsernameExistsException: Email ya registrado
   * - InvalidPasswordException: Contraseña no cumple requisitos
   * - InvalidParameterException: Parámetro inválido (email mal formado)
   * - LimitExceededException: Demasiados intentos
   * - CodeDeliveryFailureException: Error enviando código
   */
  private async handleSaveEmailError(error: any, newEmail: string): Promise<void> {
    const errorName = error?.name || error?.code || '';
    const errorMessage = error?.message || '';

    // Email ya existe en Cognito
    if (errorName === 'UsernameExistsException') {
      try {
        // Intentar reenviar código (usuario pudo registrarse pero no verificar)
        await this.cognitoService.resendConfirmationCode(newEmail);
        this.signupState.setFormData({
          ...this.signupState.currentState.formData!,
          email: newEmail,
        });
        this.email = newEmail;
        this.showEditEmail = false;
        this.signupState.setEmailOtpSentAt(Date.now());
        this.onSuccess.emit('Código reenviado al nuevo correo');
        this.startCountdown();
      } catch (resendError: any) {
        // Si falla el reenvío, el email ya está verificado por otro usuario
        if (resendError?.name === 'LimitExceededException') {
          this.onError.emit('Demasiados intentos. Espera un momento.');
        } else {
          this.onError.emit('Este correo ya está registrado y verificado.');
        }
      }
      return;
    }

    // Contraseña no cumple requisitos
    if (errorName === 'InvalidPasswordException') {
      this.onError.emit('La contraseña no cumple con los requisitos de seguridad.');
      return;
    }

    // Email mal formado
    if (errorName === 'InvalidParameterException') {
      if (errorMessage.includes('email')) {
        this.onError.emit('El formato del correo no es válido.');
      } else {
        this.onError.emit('Los datos ingresados no son válidos.');
      }
      return;
    }

    // Demasiados intentos
    if (errorName === 'LimitExceededException') {
      this.onError.emit('Demasiados intentos. Espera un momento antes de reintentar.');
      return;
    }

    // Error enviando código
    if (errorName === 'CodeDeliveryFailureException') {
      this.onError.emit('Error al enviar el código. Verifica que el correo sea válido.');
      return;
    }

    // Error de rate limit del backend de validación
    if (error?.statusCode === 429) {
      this.onError.emit('Demasiadas solicitudes. Espera un momento.');
      return;
    }

    // Error genérico
    this.onError.emit('Error al cambiar el correo. Intenta nuevamente.');
  }

  cancelEditEmail(): void {
    this.showEditEmail = false;
    this.editEmailForm.reset();
  }

  // ==================== NAVEGACIÓN ====================

  async goBack(): Promise<void> {
    await this.signupState.prevStep();
  }
}
